import { z } from "zod";
import { NoAuthError } from "@utils/NoAuthError";
import { RouterOutputs, trpc } from "@utils/trpc";
import { getQueryKey } from "@trpc/react-query";
import {
  recentSearchesListSchema,
  recentSearchesPaginatedSchema,
} from "@libs/wire";
import type { DataSession } from "../../../data/core/session";

export type RecentSearchesQueryData =
  RouterOutputs["user"]["listRecentSearches"];

/**
 * Compile-time drift check, bidirectional. See useLibrary/queryFunctions.ts
 * for the full rationale.
 */
type _RecentSearchesWireSchemaSubset =
  z.infer<typeof recentSearchesListSchema> extends RecentSearchesQueryData
    ? true
    : never;
type _RecentSearchesWireSchemaSuperset =
  RecentSearchesQueryData extends z.infer<typeof recentSearchesListSchema>
    ? true
    : never;
const _recentSearchesWireSchemaDriftCheck: [
  _RecentSearchesWireSchemaSubset,
  _RecentSearchesWireSchemaSuperset,
] = [true, true];
void _recentSearchesWireSchemaDriftCheck;

/**
 * Query key includes `limit` so different limits get isolated cache rows.
 * (e.g. one screen requests `limit: 5`, another requests `limit: 20` —
 * each gets its own SQLite row.)
 */
export const recentSearchesQueryKey = (limit?: number) =>
  getQueryKey(trpc.user.listRecentSearches, { limit });

export const recentSearchesQuery = (data: DataSession, limit?: number) =>
  data.cache.durableQueryOptions({
    queryKey: recentSearchesQueryKey(limit),
    queryFn: () => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("recentSearchesQuery queryFn");
      }
      return data.api.user.listRecentSearches.query({ limit });
    },
    schema: recentSearchesListSchema,
    staleTime: 1000 * 60 * 5,
  });

export type RecentSearchesPaginatedPage =
  RouterOutputs["user"]["listRecentSearchesPaginated"];

type _RecentSearchesPaginatedWireSchemaSubset =
  z.infer<
    typeof recentSearchesPaginatedSchema
  > extends RecentSearchesPaginatedPage
    ? true
    : never;
type _RecentSearchesPaginatedWireSchemaSuperset =
  RecentSearchesPaginatedPage extends z.infer<
    typeof recentSearchesPaginatedSchema
  >
    ? true
    : never;
const _recentSearchesPaginatedWireSchemaDriftCheck: [
  _RecentSearchesPaginatedWireSchemaSubset,
  _RecentSearchesPaginatedWireSchemaSuperset,
] = [true, true];
void _recentSearchesPaginatedWireSchemaDriftCheck;

export const recentSearchesPaginatedQueryKey = (limit: number) =>
  getQueryKey(trpc.user.listRecentSearchesPaginated, { limit });

export const recentSearchesPaginatedQuery = (
  data: DataSession,
  limit: number,
) => ({
  queryKey: recentSearchesPaginatedQueryKey(limit),
  queryFn: ({ pageParam }: { pageParam?: number }) => {
    return data.utils.user.listRecentSearchesPaginated.fetch({
      cursor: pageParam ?? 0,
      limit,
    });
  },
  getNextPageParam: (lastPage: RecentSearchesPaginatedPage) =>
    lastPage.nextCursor ?? undefined,
  initialPageParam: 0,
});
